import { ReactNode, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { useIsDesktop } from '@hooks/useMediaQuery'

interface LayoutProps {
  readonly children: ReactNode
}

export function Layout({ children }: Readonly<LayoutProps>) {
  const location = useLocation()
  const isDesktop = useIsDesktop()
  const [isSidebarOpen, setIsSidebarOpen] = useState(isDesktop)
  
  // Em mobile: fechar sidebar ao mudar de página
  useEffect(() => {
    if (!isDesktop) {
      setIsSidebarOpen(false)
    }
  }, [location.pathname, isDesktop])
  
  // Ao passar para desktop, abrir; em mobile, começar fechado
  useEffect(() => {
    setIsSidebarOpen(isDesktop)
  }, [isDesktop])
  
  const sidebarWidth = isDesktop && isSidebarOpen ? 256 : 0
  const sidebarCollapsedWidth = isDesktop && !isSidebarOpen ? 12 : 0
  const mainMarginLeft = isDesktop ? (isSidebarOpen ? sidebarWidth : sidebarCollapsedWidth) : 0

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Fixed Header - full viewport width on mobile */}
      <div className="fixed top-0 left-0 right-0 z-40 w-full min-w-0">
        <Header />
      </div>
      
      {/* Mobile: barra de toggle entre header e conteúdo */}
      {!isDesktop && (
        <div className="fixed left-0 right-0 z-30 flex justify-center pt-16 bg-[#17253C] rounded-b-2xl">
          <Sidebar
            isOpen={isSidebarOpen}
            onToggle={setIsSidebarOpen}
            isDesktop={false}
          />
        </div>
      )}
      
      {/* Desktop: sidebar lateral fixa */}
      {isDesktop && (
        <div className="fixed left-0 top-20 bottom-0 z-30">
          <Sidebar
            isOpen={isSidebarOpen}
            onToggle={setIsSidebarOpen}
            isDesktop={true}
          />
        </div>
      )}
      
      {/* Scrollable Main Content */}
      <main
        className="flex-1 overflow-y-auto transition-all duration-300"
        style={{
          marginLeft: mainMarginLeft,
          marginTop: !isDesktop
            ? 'calc(4rem + var(--sidebar-mobile-height, 56px))'
            : '5rem',
          padding: isDesktop ? '1rem' : '0.75rem 1rem 1rem',
          height: !isDesktop
            ? 'calc(100vh - 4rem - var(--sidebar-mobile-height, 56px))'
            : 'calc(100vh - 5rem)',
          maxHeight: !isDesktop
            ? 'calc(100vh - 4rem - var(--sidebar-mobile-height, 56px))'
            : 'calc(100vh - 5rem)',
        }}
      >
        {children}
      </main>
    </div>
  )
}
